import { jsx as _jsx, jsxs as _jsxs } from "preact/jsx-runtime";
import { useMemo, useState, useCallback } from 'preact/compat';
import useGlobalEvent from 'beautiful-react-hooks/useGlobalEvent';
import useDidMount from 'beautiful-react-hooks/useDidMount';
import { PaymentButton, Title } from '@widgets/shared/components/QuickPay/styles';
import { Wrapper } from './styles';
function QuickPay(props) {
    const [errorMessage, setErrorMessage] = useState(undefined);
    const { analyticsTitle, analyticsUrl, analyticsPath, products, termsAccepted, title, onClickedWhenDisabled, handleGoogleDidFail, handleAppleDidFail, appleHidden, googleHidden, disabled, paymentButtonsReady, darkButtons, } = props;
    const isDisabled = useMemo(() => disabled ?? false, [disabled]);
    const [isWorking, setIsWorking] = useState(false);
    const triggerQuickPay = useCallback((type) => {
        if (!termsAccepted) {
            if (onClickedWhenDisabled) {
                onClickedWhenDisabled();
            }
            return false;
        }
        // console.log('Products: ', products);
        // remove products that have 0 total visitors.
        setIsWorking(true);
        // remove the error message if one was previously set
        setErrorMessage(undefined);
        window.dispatchEvent(new CustomEvent(`${type}.payment`, {
            detail: products,
        }));
        return true;
    }, [onClickedWhenDisabled, products, termsAccepted]);
    const onAppleFailed = useGlobalEvent('apple.failed');
    const onAppleAvailable = useGlobalEvent('apple.available');
    const onGoogleFailed = useGlobalEvent('google.failed');
    const onGoogleAvailable = useGlobalEvent('google.available');
    const onPaymentSuccess = useGlobalEvent('payment.success');
    const onPaymentError = useGlobalEvent('payment.error');
    const onPaymentCancel = useGlobalEvent('payment.cancel');
    const sendToAnalytics = useCallback(() => {
        if (typeof gtag !== 'undefined') {
            gtag('event', 'page_view', {
                page_title: `${analyticsTitle} - Mobile payment`,
                page_location: `${analyticsUrl}complete`,
                page_path: `${analyticsPath}complete`,
                send_to: 'UA-44012490-1',
            });
        }
    }, [analyticsPath, analyticsTitle, analyticsUrl]);
    const handleGoogleAvailable = useCallback(() => {
        if (handleGoogleDidFail) {
            handleGoogleDidFail(false);
        }
    }, [handleGoogleDidFail]);
    const handleAppleAvailable = useCallback(() => {
        if (handleAppleDidFail) {
            handleAppleDidFail(false);
        }
    }, [handleAppleDidFail]);
    const handleGoogleFailed = useCallback(() => {
        if (handleGoogleDidFail) {
            handleGoogleDidFail(true);
        }
    }, [handleGoogleDidFail]);
    const handleAppleFailed = useCallback(() => {
        if (handleAppleDidFail) {
            handleAppleDidFail(true);
        }
    }, [handleAppleDidFail]);
    onGoogleFailed(handleGoogleFailed);
    onAppleFailed(handleAppleFailed);
    onGoogleAvailable(handleGoogleAvailable);
    onAppleAvailable(handleAppleAvailable);
    onPaymentSuccess((event) => {
        const { successUrl } = event.detail;
        setIsWorking(false);
        sendToAnalytics();
        if (successUrl) {
            window.location.href = successUrl;
        }
    });
    onPaymentError((event) => {
        const { errorMessage: paymentErrorMessage } = event.detail;
        setIsWorking(false);
        setErrorMessage(paymentErrorMessage);
    });
    onPaymentCancel(() => { setIsWorking(false); });
    const handleDidMount = useCallback(() => {
        // if the buttons have not been initialised by another component - i.e. submission wrapper within booking form.
        if (!paymentButtonsReady) {
            window.dispatchEvent(new Event('PaymentButtonsLoaded'));
        }
    }, [paymentButtonsReady]);
    useDidMount(handleDidMount);
    if (appleHidden && googleHidden) {
        return null;
    }
    return (_jsxs(Wrapper, { children: [errorMessage && _jsx("p", { children: errorMessage }), title && _jsx(Title, { children: title }), !appleHidden && (_jsxs(PaymentButton, { type: "button", isDisabled: !termsAccepted || isWorking || isDisabled, onClick: () => triggerQuickPay('apple'), isDark: darkButtons, children: ["Pay with", darkButtons ? (_jsx("img", { src: "/assets/images/apple_pay--white.png", alt: "Apple pay" })) : (_jsx("img", { src: "/assets/images/apple_pay.png", alt: "Apple pay" }))] })), !googleHidden && (_jsxs(PaymentButton, { type: "button", isDisabled: !termsAccepted || isWorking || isDisabled, onClick: () => triggerQuickPay('google'), isDark: darkButtons, children: ["Pay with", darkButtons ? (_jsx("img", { src: "/assets/images/google_pay--white.png", alt: "Google pay" })) : (_jsx("img", { src: "/assets/images/google_pay.png", alt: "Google pay" }))] }))] }));
}
QuickPay.defaultProps = {
    analyticsTitle: 'Quick Pay',
    analyticsUrl: 'https://www.snowdome.co.uk/',
    analyticsPath: '/',
    title: undefined,
    onClickedWhenDisabled: undefined,
    handleAppleDidFail: undefined,
    handleGoogleDidFail: undefined,
    disabled: false,
    paymentButtonsReady: false,
    darkButtons: false,
};
export default QuickPay;
